import { Directive, ElementRef, HostBinding, HostListener, Input, OnInit, Renderer2 } from "@angular/core";
import { UntypedFormGroup, NG_VALIDATORS, Validator, ValidationErrors } from "@angular/forms";
import { TemplateMustMatch } from "./validators.directive";
import { Dropdown } from "primeng/dropdown";
import { InputNumber } from "primeng/inputnumber";
import { MultiSelect } from "primeng/multiselect";


/**
 * Add a "data-testid" attribute to the control using the value of "id".
 * For now, we use this for PrimeNG controls that do not output an id attribute.
 */
@Directive({
  selector: '[appTid]'
})
export class ScrTestIdDirective {
  @HostBinding('attr.data-testid') @Input() id = '';
}


/**
 * For dropdowns, add a "inputId" attribute if ID is set. and also the testid.
 */
@Directive({
  selector: '[appMs]'
})
export class ScrMultiSelectDirective implements OnInit {
  @Input() id!: string | null;

  constructor(private ms: MultiSelect, private el: ElementRef) {
  }

  ngOnInit(): void {
    if (this.ms && this.id) {
      this.ms.inputId = `${this.id}Input`;
    }

    const elmt = this.el.nativeElement.closest('div') as HTMLDivElement;
    if (elmt) {
      let labels = elmt.getElementsByTagName('label');
      // if no labels found, try one level higher.
      if (labels.length === 0) {
        if (elmt.parentElement) {
          labels = elmt.parentElement.getElementsByTagName('label');
        }
      }
      if (labels.length > 0) {
        const lbl = labels[0] as HTMLLabelElement;
        if (lbl.htmlFor === this.id) {
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          const self = this;
          lbl.addEventListener<"click">('click', () => {
            self.ms.focusInputViewChild?.nativeElement.focus();  
          });
        }
      }
    }
  }
}


/**
 * For dropdowns, add a "inputId" attribute if ID is set. and also the testid.
 */
@Directive({
  selector: '[appDd]'
})
export class ScrDropdownDirective implements OnInit {
  @Input() id!: string | null;

  constructor(private dd: Dropdown, private el: ElementRef) {

  }

  ngOnInit(): void {
    if (this.dd && this.id) {
      this.dd.inputId = `${this.id}Input`;
    }

    // primeng does not set the input id of the input field.
    if (this.dd && this.id && this.dd.editable) {
      setTimeout(() => {
        if (this.dd) {
          // console.debug('after timeout got ' + this.dd.editableInputViewChild);
          const elmt = this.dd.editableInputViewChild?.nativeElement as HTMLInputElement;
          if (elmt) {
            elmt.autocomplete = 'off';
            if (!elmt.id) {
              elmt.id = `${this.id}Input`;
            }
          }
        }
      }, 100);
    }

    const elmt = this.el.nativeElement.closest('div') as HTMLDivElement;
    if (elmt) {
      let labels = elmt.getElementsByTagName('label');
      // if no labels found, try one level higher.
      if (labels.length === 0) {
        if (elmt.parentElement) {
          labels = elmt.parentElement.getElementsByTagName('label');
        }
      }
      if (labels.length > 0) {
        const lbl = labels[0] as HTMLLabelElement;
        if (lbl.htmlFor === this.id) {
          // eslint-disable-next-line @typescript-eslint/no-this-alias
          const self = this;
          lbl.addEventListener<"click">('click', () => {
            self.dd.focus();
          });
        }
      }
    }
  }
  @HostBinding('attr.data-testid')
  public get testId(): string | null {
    return this.id;
  }
}


/**
 * For input number, add a "inputId" attribute if ID is set. and also the testid.
 */
@Directive({
  selector: '[appIn]'
})
export class ScrInputNumberDirective implements OnInit {
  @Input() id!: string | null;

  constructor(private inputNumber: InputNumber) {
  }

  ngOnInit(): void {
    if (this.inputNumber && this.id) {
      this.inputNumber.inputId = `${this.id}Input`;
    }
  }
  @HostBinding('attr.data-testid')
  public get testId(): string | null {
    return this.id;
  }
}



@Directive({
  selector: '[appScrText]'
})
export class ScrTextDirective {
  @Input() id: string | null = null;

  @HostBinding('class.form-control') formControl = true;
  @HostBinding('class.form-text') formText = true;
  @HostBinding('type') type = 'text';
  @HostBinding('attr.data-testid')
  public get testId(): string | null {
    return this.id;
  }

  @HostListener('blur') onblur() {
    if (this.el) {
      const val = this.el.nativeElement.value;

      if (/^\s+$/.test(val)) {
        this.el.nativeElement.value = '';
        this.el.nativeElement.dispatchEvent(new Event('input'));
      }
    }
  }

  constructor(private el: ElementRef) { }
}

@Directive({
  selector: '[appPassword]'
})
export class ScrPasswordDirective {
  @HostBinding('class.form-control') formControl = true;
  @HostBinding('class.form-text') formText = true;
  @HostBinding('type') type = 'password';
  @HostBinding('attr.data-testid') @Input() id = '';
}

@Directive({
  selector: '[appEmail]'
})
export class ScrEmailDirective {
  @HostBinding('class.form-control') formControl = true;
  @HostBinding('class.form-text') formText = true;
  @HostBinding('type') type = 'email';
}

@Directive({
  selector: '[appZip]'
})
export class ZipCodeDirective {
  @Input() country?: string;
  constructor(private el: ElementRef, private renderer: Renderer2) { }

  @HostListener('input', ['$event']) onInput(event: { target: { value: string; }; }) {
    if (this.country === 'ca') {
      this.el.nativeElement.value = event.target.value.toUpperCase();
    }
  }
}

@Directive({
  selector: '[appUppercase]'
})
export class UpperCaseDirective {

  lastValue?: string;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input('appUppercase') options: any;


  constructor(private ref: ElementRef) { }

  @HostListener('input', ['$event']) onInput(event: InputEvent) {
    if (this.options && !this.options.apply) {
      return;
    }
    const target = event.target as HTMLInputElement;
    const start = target?.selectionStart;
    const end = target?.selectionEnd;
    target.value = target.value.toUpperCase();
    target.setSelectionRange(start, end);
    event.preventDefault();

    if (!this.lastValue || (this.lastValue && target.value.length > 0 && this.lastValue !== target.value)) {
      this.lastValue = this.ref.nativeElement.value = target.value;
      // Propagation
      const evt = document.createEvent('HTMLEvents');
      evt.initEvent('input', false, true);
      target.dispatchEvent(evt);
    }
  }
}

@Directive({
  selector: '[appNumericOnly]'
})
export class NumericOnlyDirective {

  name!: string;

  @Input('appNumericOnly') option: boolean | string | undefined;

  constructor(private el: ElementRef, private renderer: Renderer2) {
    this.name = el.nativeElement.name;
    renderer.listen(el.nativeElement, 'paste', (event) => {
      if (this.option === false || this.option === 'false') {
        return true;
      }

      if (event.clipboardData) {
        const paste = event.clipboardData.getData('text');
        return !isNaN(paste);
      }
      return true;
    });
    renderer.listen(el.nativeElement, 'keypress', (event) => {
      if (this.option === false || this.option === 'false') {
        return true;
      }

      if (event.ctrlKey || event.metaKey || event.altKey) {
        return true;
      }
      const charCode = (event.which) ? event.which : event.keyCode;
      const isAlpha = ((charCode >= 32) && ((charCode <= 47)) || (charCode > 57));
      return !isAlpha;
    });

  }
}


@Directive({
  selector: '[appMustMatch]',
  providers: [{ provide: NG_VALIDATORS, useExisting: MustMatchDirective, multi: true }]
})
export class MustMatchDirective implements Validator {
  @Input() appMustMatch: string[] = [];

  validate(formGroup: UntypedFormGroup): ValidationErrors | null {
    return TemplateMustMatch(this.appMustMatch[0], this.appMustMatch[1])(formGroup);
  }
}
